import React, { useEffect, useState } from 'react';
import {
    Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, CircularProgress,
    MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead,
    TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import RegisterNotes from './RegisterNotes';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { StatutoryRegisterService } from '../../services/statutoryRegister.service';
import { HourBreachType, IHourBreachReport } from '../../interfaces/statutoryRegister.interface';
import { useToast } from '../../context/ToastContext';
import { MONTH_NAMES, triggerDownload } from './shared';

interface Props {
    institutionId: string;
}

const currentYear = () => new Date().getFullYear();
const ALL_MONTHS = '';
const EMPLOYEE_TOTALS_PAGE_SIZE = 10;

const BREACH_TYPE_LABELS: Record<HourBreachType, string> = {
    DAILY_10H: 'Daily > 10h',
    WEEKLY_60H: 'Weekly > 60h',
    ANNUAL_AVG_56H: 'Annual Avg > 56h/week',
};

/**
 * Fifth statutory register tab: working-hour breach report (Bangladesh Labour Act §100/§102).
 * Mirrors {@code OvertimeRegisterTab}'s conventions exactly (year/month filters, load-then-render,
 * download-uses-loaded-period, per-employee totals in a collapsed section) plus a disclosures
 * accordion for this report's D6/D8 caveat counters, and the caveats list ALWAYS renders (D6/D12
 * — even when every count is zero, per the spec's sourcing lesson).
 */
const HourBreachReportTab: React.FC<Props> = ({ institutionId }) => {
    const toast = useToast();
    const [year, setYear] = useState(currentYear());
    const [month, setMonth] = useState<number | ''>(ALL_MONTHS);
    const [report, setReport] = useState<IHourBreachReport | null>(null);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [downloading, setDownloading] = useState(false);
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalsPage, setTotalsPage] = useState(0);

    const load = async () => {
        if (Number.isNaN(year)) return;
        setLoading(true);
        setPage(0);
        setTotalsPage(0);
        try {
            const data = await StatutoryRegisterService.getHourBreachReport(
                institutionId, year, month === '' ? undefined : month,
            );
            setReport(data);
            setLoadError(null);
        } catch (e) {
            setReport(null);
            setLoadError('Could not load the hour-breach report.');
            toast.showApiError(e, 'Failed to load hour-breach report');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    const handleDownload = async () => {
        if (!report) return;
        setDownloading(true);
        try {
            const blob = await StatutoryRegisterService.downloadHourBreachReportXlsx(
                institutionId, report.year, report.month ?? undefined,
            );
            triggerDownload(blob, `hour-breach-report-${report.year}${report.month ? `-${report.month}` : ''}.xlsx`);
        } catch (e) {
            toast.showApiError(e, 'Download failed');
        } finally {
            setDownloading(false);
        }
    };

    const handleDownloadPdf = async () => {
        if (!report) return;
        setDownloadingPdf(true);
        try {
            const blob = await StatutoryRegisterService.downloadHourBreachReportPdf(
                institutionId, report.year, report.month ?? undefined,
            );
            triggerDownload(blob, `hour-breach-report-${report.year}${report.month ? `-${report.month}` : ''}.pdf`);
        } catch (e) {
            toast.showApiError(e, 'Download failed');
        } finally {
            setDownloadingPdf(false);
        }
    };

    const pagedRows = report
        ? report.rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        : [];
    const pagedTotals = report
        ? report.employeeTotals.slice(
            totalsPage * EMPLOYEE_TOTALS_PAGE_SIZE, totalsPage * EMPLOYEE_TOTALS_PAGE_SIZE + EMPLOYEE_TOTALS_PAGE_SIZE,
        )
        : [];

    return (
        <Box data-help-page="statutory.hourBreach.page">
            <Box data-help-section="statutory.hourBreach.filter" display="flex" gap={2} alignItems="flex-end" mb={2} flexWrap="wrap">
                <TextField
                    size="small"
                    label="Year"
                    type="number"
                    data-help="statutory.hourBreach.year"
                    value={Number.isNaN(year) ? '' : year}
                    onChange={e => {
                        const v = parseInt(e.target.value, 10);
                        if (!Number.isNaN(v)) setYear(v);
                    }}
                    sx={{ flex: '1 1 120px', maxWidth: 192 }}
                />
                <TextField
                    select size="small" label="Month" value={month} data-help="statutory.hourBreach.month"
                    onChange={e => setMonth(e.target.value === '' ? '' : parseInt(e.target.value as string, 10))}
                    sx={{ flex: '1 1 150px', maxWidth: 240 }}
                >
                    <MenuItem value="">All months</MenuItem>
                    {MONTH_NAMES.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                </TextField>
                <Button data-help="statutory.hourBreach.load" variant="outlined" size="small" onClick={load} disabled={loading}>Load</Button>
                <Button data-help="statutory.hourBreach.downloadXlsx"
                    variant="contained" size="small" startIcon={<FileDownloadIcon />}
                    onClick={handleDownload} disabled={!report || downloading}
                >
                    Download XLSX
                </Button>
                <Button data-help="statutory.hourBreach.downloadPdf"
                    variant="outlined" size="small" startIcon={<FileDownloadIcon />}
                    onClick={handleDownloadPdf} disabled={!report || downloadingPdf}
                >
                    Download PDF
                </Button>
            </Box>

            {loading && <CircularProgress size={24} />}

            {!loading && loadError && (
                <Alert severity="error" sx={{ mb: 2 }} data-testid="hour-breach-load-error">{loadError}</Alert>
            )}

            {!loading && report && (
                <>
                    {/* D6/D12: caveats ALWAYS render, including when every disclosure count is zero. */}
                    <RegisterNotes notes={report.caveats} testId="hour-breach-caveats" />

                    <TableContainer component={Paper} data-testid="hour-breach-table" data-help-section="statutory.hourBreach.table">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell data-help="statutory.hourBreach.col.employee">Employee</TableCell>
                                    <TableCell data-help="statutory.hourBreach.col.code">Code</TableCell>
                                    <TableCell data-help="statutory.hourBreach.col.type">Breach Type</TableCell>
                                    <TableCell data-help="statutory.hourBreach.col.period">Period</TableCell>
                                    <TableCell align="right" data-help="statutory.hourBreach.col.minutes">Minutes</TableCell>
                                    <TableCell align="right" data-help="statutory.hourBreach.col.hours">Hours</TableCell>
                                    <TableCell align="right" data-help="statutory.hourBreach.col.limit">Limit (min)</TableCell>
                                    <TableCell align="right" data-help="statutory.hourBreach.col.exceeded">Exceeded By (min)</TableCell>
                                    <TableCell align="right" data-help="statutory.hourBreach.col.beyondEight">Hours Beyond 8h</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {pagedRows.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center">
                                            <Typography color="text.secondary">No breaches for this period.</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {pagedRows.map((row, idx) => (
                                    <TableRow key={`${row.userId}-${row.breachType}-${row.period}-${idx}`} hover>
                                        <TableCell>{row.employeeName ?? '—'}</TableCell>
                                        <TableCell>{row.employeeCode ?? '—'}</TableCell>
                                        <TableCell>
                                            <Chip size="small" label={BREACH_TYPE_LABELS[row.breachType]} />
                                        </TableCell>
                                        <TableCell>{row.period}</TableCell>
                                        <TableCell align="right">{row.minutes}</TableCell>
                                        <TableCell align="right">{row.hours.toFixed(2)}</TableCell>
                                        <TableCell align="right">{row.limitMinutes}</TableCell>
                                        <TableCell align="right">{row.exceededByMinutes}</TableCell>
                                        <TableCell align="right">
                                            {row.hoursBeyondEightH === null ? '—' : row.hoursBeyondEightH.toFixed(2)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                            <TableFooter>
                                <TableRow data-help="statutory.hourBreach.grandTotal" sx={{ '& td': { fontWeight: 'bold', borderTop: '2px solid', borderColor: 'divider' } }}>
                                    <TableCell colSpan={3}>Grand Total</TableCell>
                                    <TableCell data-testid="hb-grand-daily">Daily: {report.grandTotal.daily10hCount}</TableCell>
                                    <TableCell data-testid="hb-grand-weekly">Weekly: {report.grandTotal.weekly60hCount}</TableCell>
                                    <TableCell data-testid="hb-grand-annual">Annual: {report.grandTotal.annualAvg56hCount}</TableCell>
                                    <TableCell colSpan={3} />
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={report.rows.length}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    />

                    <Accordion sx={{ mt: 2 }} data-testid="hour-breach-employee-totals-section" data-help-section="statutory.hourBreach.employeeTotals">
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>Employee Totals ({report.employeeTotals.length})</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell data-help="statutory.hourBreach.totals.col.employee">Employee</TableCell>
                                            <TableCell align="right" data-help="statutory.hourBreach.totals.col.daily">Daily 10h</TableCell>
                                            <TableCell align="right" data-help="statutory.hourBreach.totals.col.weekly">Weekly 60h</TableCell>
                                            <TableCell data-help="statutory.hourBreach.totals.col.annual">Annual Avg 56h</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {pagedTotals.map(t => (
                                            <TableRow key={t.userId}>
                                                <TableCell>{t.employeeName ?? t.userId}</TableCell>
                                                <TableCell align="right">{t.daily10hCount}</TableCell>
                                                <TableCell align="right">{t.weekly60hCount}</TableCell>
                                                <TableCell>
                                                    {t.annualAvgNotEvaluable
                                                        ? <Chip size="small" label="Not evaluable (<8 weeks)" />
                                                        : (t.annualAvgBreach
                                                            ? <Chip size="small" color="error" label="Breach" />
                                                            : <Chip size="small" color="success" label="Clean" />)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                component="div"
                                count={report.employeeTotals.length}
                                page={totalsPage}
                                onPageChange={(_, p) => setTotalsPage(p)}
                                rowsPerPage={EMPLOYEE_TOTALS_PAGE_SIZE}
                                rowsPerPageOptions={[EMPLOYEE_TOTALS_PAGE_SIZE]}
                            />
                        </AccordionDetails>
                    </Accordion>

                    <Accordion sx={{ mt: 2 }} data-testid="hour-breach-disclosures-section" data-help-section="statutory.hourBreach.disclosures">
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>Disclosures</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Table size="small">
                                <TableBody>
                                    <TableRow>
                                        <TableCell data-help="statutory.hourBreach.disclosure.evaluated">Employees Evaluated</TableCell>
                                        <TableCell align="right">{report.grandTotal.employeesEvaluated}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell data-help="statutory.hourBreach.disclosure.affected">Employees Affected</TableCell>
                                        <TableCell align="right">{report.grandTotal.employeesAffected}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell data-help="statutory.hourBreach.disclosure.notEvaluable">Annual Average Not Evaluable</TableCell>
                                        <TableCell align="right">{report.grandTotal.notEvaluableCount}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell data-help="statutory.hourBreach.disclosure.incomplete">Days With Incomplete Punches</TableCell>
                                        <TableCell align="right">{report.grandTotal.daysWithIncompletePunches}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell data-help="statutory.hourBreach.disclosure.negative">Days With Negative Span</TableCell>
                                        <TableCell align="right">{report.grandTotal.daysWithNegativeSpan}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell data-help="statutory.hourBreach.disclosure.suppressed">Suppressed (Soft-Deleted) Days</TableCell>
                                        <TableCell align="right">{report.grandTotal.suppressedDays}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell data-help="statutory.hourBreach.disclosure.flagged">Flagged-Location Days</TableCell>
                                        <TableCell align="right">{report.grandTotal.flaggedLocationDays}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell data-help="statutory.hourBreach.disclosure.noProfile">Members Without Profile</TableCell>
                                        <TableCell align="right">{report.grandTotal.membersWithoutProfile}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell data-help="statutory.hourBreach.disclosure.multiInstitution">Multi-Institution Members</TableCell>
                                        <TableCell align="right">{report.grandTotal.multiInstitutionMembers}</TableCell>
                                    </TableRow>
                                    {report.grandTotal.orphanPunchCount !== null && (
                                        <TableRow>
                                            <TableCell data-help="statutory.hourBreach.disclosure.orphan">Orphan Punches (blocked by policy)</TableCell>
                                            <TableCell align="right">{report.grandTotal.orphanPunchCount}</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </AccordionDetails>
                    </Accordion>
                </>
            )}
        </Box>
    );
};

export default HourBreachReportTab;
